// Which inbox threads belong to a confirmed Ongoing, and what status that story wears (Pouria,
// 2026-09-02: the mailbox row's dot is an Ongoing-status dot now — see lib/emailListingRow.js).
//
// The answer comes from ONE bulk read, `POST v1/ongoing/timeline-status` with the page's timeline
// ids, never a story read per row: the BFF folds every confirmed membership into
// [{ timelineId, ongoingId, status }] and a thread in several stories answers once, already wearing
// the most urgent status. This module is the pure half — the reading of that answer into the map
// the rows look themselves up in; store/modules/ongoingMembership.js does the fetching.

// What a status LOOKS like is lib/dueStatus's to say — this module never keeps a palette of its own.
import { statusMeta } from './dueStatus'

// The BFF path under the `ongoing` service (api.insertWithPath('ongoing', TIMELINE_STATUS_PATH, …)).
export const TIMELINE_STATUS_PATH = 'timeline-status'

// Guids arrive in whatever casing the serializer chose, and the listing's entity ids have their
// own history of casing — every key is lowercased so the two can never miss each other over case.
const key = (id) => String(id || '').toLowerCase()

// The membership map: lowercased timelineId → the ongoing's ladder status ("needsAttention",
// "waiting", …, as lib/dueStatus reads them). Rows that name no thread or no status are dropped —
// a dot with nothing behind it says nothing. Anything unreadable (a failed fetch hands `undefined`
// here) is the empty map, which is exactly "no dots".
export const membershipMap = (rows) => {
  const map = {}
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row || !row.timelineId || !row.status) continue
    map[key(row.timelineId)] = row.status
  }
  return map
}

// The row's own lookup: this thread's ongoing status, or null when it belongs to no story (which
// is most rows, and means no dot).
export const membershipStatus = (map, timelineId) =>
  (map && timelineId && map[key(timelineId)]) || null

// ── The indicator itself (busy-my-web#320, Pouria 2026-09-03) ────────────────────────────────────
// "Those blue things on emails view only show if that email's connected to ongoing and the colour is
// dependent on ongoing status." Every email surface — the mailbox listing's dot, the folder pages'
// rows, the workspace's email rows, the Briefing's thread rows — asks THIS function whether to draw
// anything and, if so, in what colour. There is no second palette anywhere: the colour is
// lib/dueStatus's `statusMeta`, the same file the story rows, the chips and the headers read.

// A SUGGESTION is not a connection (#317's cards, and the issue's rule 4): Busy thinks these emails
// might belong to a story and is asking. It therefore never wears a ladder colour — a grey chip that
// says what it is, so a maybe can never be misread across the room as a yes.
export const SUGGESTED_COLOUR = '#9ca3af'
export const SUGGESTED_TINT = 'rgba(156,163,175,0.12)'
export const SUGGESTED_LABEL = 'Possible match'

// The chip's fill: the status' own colour at 8% — the tint every chip in the estate is drawn with
// (bg-[#3741f3]/[0.08] in the templates), derived rather than listed so a new rung needs no entry.
export const tintOf = (colour) => {
  const hex = String(colour || '').trim().replace('#', '')
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return 'rgba(55,65,243,0.08)'
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16))
  return `rgba(${r},${g},${b},0.08)`
}

/**
 * What an email row/card should draw, or NULL for "draw nothing".
 *
 * Null is the common answer and the point of the whole issue: most mail belongs to no story, and an
 * indicator on it is a claim that isn't true. A confirmed membership answers with the ladder colour;
 * an unconfirmed suggestion answers with the grey "Possible match" treatment and no status at all.
 *
 * @param status the ongoing's ladder status from the membership map, or null.
 * @param suggested true where the only thing behind the row is an unanswered suggestion.
 */
export const ongoingIndicator = (status, { suggested = false } = {}) => {
  if (status) {
    const meta = statusMeta(status)
    return {
      connected: true,
      suggested: false,
      status,
      colour: meta.color,
      tint: tintOf(meta.color),
      label: `In Ongoing — ${meta.label}`,
    }
  }
  if (!suggested) return null
  return {
    connected: false,
    suggested: true,
    status: null,
    colour: SUGGESTED_COLOUR,
    tint: SUGGESTED_TINT,
    label: SUGGESTED_LABEL,
  }
}

/** The row's whole answer in one step: the page's membership map + this thread's id → indicator. */
export const indicatorFor = (map, timelineId, opts) =>
  ongoingIndicator(membershipStatus(map, timelineId), opts)

export default membershipMap
